import React, { useEffect, useState, useMemo, useRef } from 'react';
import { X, DollarSign, Calculator, FileText, Edit3, Lock, Send, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { mensajeErrorApi } from '../apiErrors';
import FacturarButton from './FacturarButton';

const D2 = (v) => Number(Number(v || 0).toFixed(2));
const N = (v) => Number(v) || 0;

// Servicios que tienen su propio campo en la liquidación: no se ofrecen como «otro concepto».
const SERVICIOS_CON_CAMPO = ['consolidacion', 'consolidacion_china', 'entrega_domicilio', 'servicio_aduanero'];
// Peso desde el cual la entrega a domicilio va incluida (igual en todos los orígenes).
const UMBRAL_DOMICILIO_LB = 4;
const esLineaPropia = (concepto) => /aduan|onsol|domicil/i.test(concepto);

export default function LiquidarModal({ paquetes, rol, onClose, onLiquidado }) {
  const paquete = paquetes[0];
  const esConsolidado = paquetes.length > 1 || !!paquete.consolidacion_detalle;

  const [tarifas, setTarifas] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [enviandoLiq, setEnviandoLiq] = useState(false);
  // Envío de la liquidación al cliente (siempre manual, con el botón).
  const [envio, setEnvio] = useState({ a: '', en: null, desactualizado: false });
  const [totalLiquidacion, setTotalLiquidacion] = useState(0);
  const [clienteEmail, setClienteEmail] = useState('');

  // modo: 'crear' (nuevo), 'ver' (read-only), 'editar' (modificando existente)
  const [modo, setModo] = useState('crear');
  const [liquidacionId, setLiquidacionId] = useState(null);
  const [facturada, setFacturada] = useState(false);
  const [facturasElectronicas, setFacturasElectronicas] = useState([]);

  // Ingresos
  const [peso, setPeso] = useState(D2(paquete.peso_facturable || paquete.peso_real || 0));
  const [servAduanero, setServAduanero] = useState(0);
  const [consolMonto, setConsolMonto] = useState(0);
  // Entrega a domicilio: va incluida desde 4 lb (Miami y China); por debajo se cobra.
  const [entregaDom, setEntregaDom] = useState(0);
  const entregaManual = useRef(false);
  // Cobros no recurrentes que se agregan a mano: reempaque, retiro, etc. (siempre con IVA).
  const [otros, setOtros] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [aduana, setAduana] = useState(20);
  const [fodinfa, setFodinfa] = useState(0.50);
  const [gastosBancarios, setGastosBancarios] = useState(3.00);
  const [notasCliente, setNotasCliente] = useState('');
  const [formaCobro, setFormaCobro] = useState('pendiente');

  // Costos
  const [costoMtc, setCostoMtc] = useState(0);
  const [ultimaMilla, setUltimaMilla] = useState(0);
  const [consolCosto, setConsolCosto] = useState(0);
  const [reemAdCosto, setReemAdCosto] = useState(20);
  const [reemGbCosto, setReemGbCosto] = useState(0);
  const [fleteVol, setFleteVol] = useState(0);
  const [facMtcServ, setFacMtcServ] = useState('');
  const [facMtcReem, setFacMtcReem] = useState('');
  const [facEcuServ, setFacEcuServ] = useState('');
  const [facEcuReem, setFacEcuReem] = useState('');

  useEffect(() => {
    const cargarTodo = async () => {
      try {
        const [t, c, s] = await Promise.all([
          api.get('/tarifas/'),
          api.get('/cargos-reembolso/'),
          api.get('/servicios-adicionales/'),
        ]);
        const ts = t.data.results || t.data;
        const cs = c.data.results || c.data;
        const ss = s.data.results || s.data;
        setTarifas(ts);
        setServicios(ss);

        // Check if paquete already has a liquidation
        const liqId = paquete.liquidacion_activa;
        if (liqId) {
          const liqRes = await api.get(`/liquidaciones/${liqId}/`);
          const liq = liqRes.data;
          setLiquidacionId(liqId);
          setEnvio({ a: liq.correo_enviado_a || '', en: liq.correo_enviado_en || null, desactualizado: !!liq.correo_desactualizado });
          setTotalLiquidacion(Number(liq.total || 0));
          setClienteEmail((liq.cliente_detalle && liq.cliente_detalle.email) || '');

          const tieneFacturaAutorizada = Array.isArray(liq.factura_electronica)
            && liq.factura_electronica.some((f) => f.estado === 'autorizada' || f.estado === 'enviada');
          setFacturada(tieneFacturaAutorizada);
          setFacturasElectronicas(Array.isArray(liq.factura_electronica) ? liq.factura_electronica : []);
          setModo('ver');

          // Populate fields from saved liquidation
          const lineas = liq.lineas || [];
          const fleteLine = lineas.find((l) => l.tipo === 'flete');
          const servLine = lineas.find((l) => l.tipo === 'servicio' && l.concepto.toLowerCase().includes('aduan'));
          const consolLine = lineas.find((l) => l.tipo === 'servicio' && l.concepto.toLowerCase().includes('onsol'));
          const aduanaLine = lineas.find((l) => l.tipo === 'reembolso' && l.concepto.toLowerCase().includes('aduan'));
          const fodinfaLine = lineas.find((l) => l.tipo === 'reembolso' && l.concepto.toLowerCase().includes('fodinfa'));
          const gbLine = lineas.find((l) => l.tipo === 'reembolso' && l.concepto.toLowerCase().includes('banc'));

          if (fleteLine) {
            const subtotalServ = D2(Number(fleteLine.base) + (servLine ? Number(servLine.base) : 0) + (consolLine ? Number(consolLine.base) : 0));
            const ivaTotal = D2(Number(fleteLine.iva) + (servLine ? Number(servLine.iva) : 0) + (consolLine ? Number(consolLine.iva) : 0));
            const ivaPctCalc = subtotalServ > 0 ? ivaTotal / subtotalServ : 0.15;
            const pesoCalc = ivaPctCalc > 0 ? D2(Number(fleteLine.base) / (ts.find(
              (tt) => tt.pais_origen === paquete.pais_origen && tt.categoria === (paquete.categoria_aduanera || 'b') && tt.activa
            )?.tarifa_bajo_umbral || 1)) : peso;
            // We can't perfectly reverse the peso from the flete, so use peso_facturable from paquete
          }

          setPeso(liq.peso_facturable ? D2(liq.peso_facturable) : D2(paquete.peso_facturable || paquete.peso_real || 0));
          setServAduanero(servLine ? D2(servLine.base) : 0);
          setConsolMonto(consolLine ? D2(consolLine.base) : 0);
          const domLine = lineas.find((l) => l.tipo === 'servicio' && /domicil/i.test(l.concepto));
          setEntregaDom(domLine ? D2(domLine.base) : 0);
          entregaManual.current = true;
          setOtros(lineas.filter((l) => l.tipo === 'servicio' && !esLineaPropia(l.concepto))
            .map((l) => ({ concepto: l.concepto, monto: D2(l.base) })));
          setAduana(aduanaLine ? D2(aduanaLine.base) : 0);
          setFodinfa(fodinfaLine ? D2(fodinfaLine.base) : 0);
          setGastosBancarios(gbLine ? D2(gbLine.base) : 0);
          setNotasCliente(liq.notas_cliente || '');
          setFormaCobro(liq.forma_cobro || 'pendiente');

          // Load costos internos
          if (liq.costo_interno) {
            const ci = liq.costo_interno;
            setCostoMtc(D2(ci.costo_mtc));
            setUltimaMilla(D2(ci.ultima_milla));
            setFleteVol(D2(ci.flete_volumetrico));
            setConsolCosto(D2(ci.consolidacion_costo));
            setReemAdCosto(D2(ci.reembolso_aduana_costo));
            setReemGbCosto(D2(ci.reembolso_gastos_bancarios_costo));
            setFacMtcServ(ci.fac_mtc_servicio || '');
            setFacMtcReem(ci.fac_mtc_reembolso || '');
            setFacEcuServ(ci.fac_ecuafly_servicio || '');
            setFacEcuReem(ci.fac_ecuafly_reembolso || '');
          }
        } else {
          // New liquidation — load defaults
          const aduanaCargo = cs.find((x) => x.codigo === 'aduana');
          const fodinfaCargo = cs.find((x) => x.codigo === 'fodinfa');
          const gbCargo = cs.find((x) => x.codigo === 'gastos_bancarios');
          if (aduanaCargo) { setAduana(D2(aduanaCargo.monto)); setReemAdCosto(D2(aduanaCargo.monto)); }
          if (fodinfaCargo) setFodinfa(D2(fodinfaCargo.monto));
          if (gbCargo) setGastosBancarios(D2(gbCargo.monto));

          // La consolidación cuesta distinto según el origen: Miami $10, China $12.
          const consolServ = ss.find((x) => x.codigo === (paquete.pais_origen === 'china' ? 'consolidacion_china' : 'consolidacion'));
          if (esConsolidado && consolServ) setConsolMonto(D2(consolServ.precio));

          if (paquetes.length > 1) {
            const totalPeso = paquetes.reduce((s, p) => s + D2(p.peso_facturable || p.peso_real || 0), 0);
            setPeso(D2(totalPeso));
          }
        }
      } catch {
        toast.error('Error al cargar datos.');
      } finally {
        setCargando(false);
      }
    };
    cargarTodo();
  }, []);

  const soloLectura = modo === 'ver';
  const ingresosLock = soloLectura || (modo === 'editar' && facturada);
  const costosLock = soloLectura;

  const tarifa = useMemo(() => {
    return tarifas.find(
      (t) => t.pais_origen === paquete.pais_origen && t.categoria === (paquete.categoria_aduanera || 'b') && t.activa
    );
  }, [tarifas, paquete]);

  const tarifaPorLibra = useMemo(() => {
    if (!tarifa) return 0;
    const lb = Math.max(N(peso) || 1, tarifa.piso_libras || 1);
    if (tarifa.modo_calculo === 'plano_por_umbral') {
      return lb < tarifa.umbral_libras ? D2(tarifa.tarifa_bajo_umbral) : D2(tarifa.tarifa_sobre_umbral);
    }
    return 0;
  }, [tarifa, peso]);

  // ¿La entrega a domicilio va incluida? Desde 4 lb, en Miami y en China; por debajo se cobra.
  const umbralDomicilio = tarifa ? UMBRAL_DOMICILIO_LB : 0;
  const domicilioIncluido = !umbralDomicilio || N(peso) >= umbralDomicilio;
  const precioDomicilio = D2((servicios.find((x) => x.codigo === 'entrega_domicilio') || {}).precio);

  // Al liquidar, propone el cargo según el peso; si el equipo lo edita a mano, ya no se toca.
  useEffect(() => {
    if (modo !== 'crear' || cargando || entregaManual.current) return;
    setEntregaDom(umbralDomicilio && N(peso) > 0 && N(peso) < umbralDomicilio ? precioDomicilio : 0);
  }, [modo, cargando, peso, umbralDomicilio, precioDomicilio]);

  const flete = useMemo(() => {
    if (!tarifa || !N(peso)) return 0;
    const lb = Math.max(N(peso), tarifa.piso_libras || 1);
    if (tarifa.modo_calculo === 'plano_por_umbral') {
      const rate = lb < tarifa.umbral_libras ? tarifa.tarifa_bajo_umbral : tarifa.tarifa_sobre_umbral;
      return D2(lb * rate);
    }
    const adicionales = Math.max(0, lb - 1);
    return D2(Number(tarifa.tarifa_primera_libra) + adicionales * Number(tarifa.tarifa_libra_adicional));
  }, [tarifa, peso]);

  const ivaPct = tarifa ? D2(tarifa.iva_pct) / 100 : 0.15;
  const totalOtros = D2(otros.reduce((t, o) => t + N(o.monto), 0));
  const subtotal = D2(flete + N(servAduanero) + N(consolMonto) + N(entregaDom) + totalOtros);
  const iva = D2(subtotal * ivaPct);
  const totalCourier = D2(subtotal + iva);
  const totalAPagar = D2(totalCourier + N(aduana) + N(fodinfa) + N(gastosBancarios));

  const totalCostoServ = D2(N(costoMtc) + N(ultimaMilla) + N(consolCosto));
  const ivaCostos = D2(totalCostoServ * ivaPct);
  const totalCostosConIva = D2(totalCostoServ + ivaCostos);
  const totalCosto = D2(totalCostosConIva + N(reemAdCosto) + N(reemGbCosto) + N(fleteVol));
  const utilidad = D2(totalAPagar - totalCosto);
  const margen = totalAPagar > 0 ? D2((utilidad / totalAPagar) * 100) : 0;

  const guardar = async () => {
    setGuardando(true);
    const payload = {
      paquete_ids: paquetes.map((p) => p.id),
      peso_facturable: D2(peso),
      servicio_aduanero: D2(servAduanero),
      consolidacion_monto: D2(consolMonto),
      entrega_domicilio: D2(entregaDom),
      otros_conceptos: otros
        .filter((o) => (o.concepto || '').trim() && N(o.monto) > 0)
        .map((o) => ({ concepto: o.concepto.trim(), monto: D2(o.monto) })),
      aduana: D2(aduana),
      fodinfa: D2(fodinfa),
      gastos_bancarios: D2(gastosBancarios),
      notas_cliente: notasCliente,
      forma_cobro: formaCobro,
      costo_mtc: D2(costoMtc),
      ultima_milla: D2(ultimaMilla),
      consolidacion_costo: D2(consolCosto),
      reembolso_aduana_costo: D2(reemAdCosto),
      reembolso_gastos_bancarios_costo: D2(reemGbCosto),
      flete_volumetrico: D2(fleteVol),
      fac_mtc_servicio: facMtcServ,
      fac_mtc_reembolso: facMtcReem,
      fac_ecuafly_servicio: facEcuServ,
      fac_ecuafly_reembolso: facEcuReem,
    };
    try {
      let res;
      if (modo === 'editar' && liquidacionId) {
        res = await api.post(`/liquidaciones/${liquidacionId}/modificar/`, payload);
        toast.success('Liquidación actualizada.');
      } else {
        res = await api.post('/liquidaciones/liquidar/', payload);
        toast.success('Liquidación guardada. Cuando esté completa, envíala al cliente con «Enviar liquidación al cliente».');
      }
      onLiquidado(res.data);
    } catch (err) {
      toast.error(mensajeErrorApi(err, 'Error al guardar la liquidación.'));
    } finally {
      setGuardando(false);
    }
  };

  const [guardandoFormaCobro, setGuardandoFormaCobro] = useState(false);

  const cambiarFormaCobro = async (valor) => {
    setFormaCobro(valor);
    if (!liquidacionId) return; // todavía no existe la liquidación, se guarda junto con el resto
    setGuardandoFormaCobro(true);
    try {
      await api.post(`/liquidaciones/${liquidacionId}/actualizar_forma_cobro/`, { forma_cobro: valor });
      toast.success('Forma de cobro actualizada.');
    } catch {
      toast.error('No se pudo actualizar la forma de cobro.');
    } finally {
      setGuardandoFormaCobro(false);
    }
  };

  const fechaEnvio = (iso) => (iso ? new Date(iso).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' }) : '');

  const enviarLiquidacion = async () => {
    const destino = clienteEmail || 'el correo registrado del cliente';
    const aviso = envio.en
      ? `Esta liquidación ya se envió el ${fechaEnvio(envio.en)} a ${envio.a}. ¿Reenviarla ahora con los valores actuales?`
      : `Se enviará la liquidación${totalLiquidacion ? ` (total $${totalLiquidacion.toFixed(2)})` : ''} a ${destino}, con copia a info@ecuafly.com. ¿Continuar?`;
    if (!window.confirm(aviso)) return;
    setEnviandoLiq(true);
    try {
      const res = await api.post(`/liquidaciones/${liquidacionId}/enviar_liquidacion/`);
      const liq = res.data;
      setEnvio({ a: liq.correo_enviado_a || '', en: liq.correo_enviado_en || null, desactualizado: !!liq.correo_desactualizado });
      toast.success(`Liquidación enviada a ${liq.correo_enviado_a} (copia a info@ecuafly.com).`);
    } catch (err) {
      toast.error(mensajeErrorApi(err, 'No se pudo enviar la liquidación.'));
    } finally {
      setEnviandoLiq(false);
    }
  };

  const field = (label, value, onChange, opts = {}) => {
    const locked = opts.readOnly || (opts.lock !== undefined ? opts.lock : soloLectura);
    const prefix = opts.unit === 'lb' ? '' : '$';
    const suffix = opts.unit === 'lb' ? ' lb' : '';
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}>
        <span className="text-sm">{label}</span>
        {locked ? (
          <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{prefix}{typeof value === 'number' ? value.toFixed(2) : value}{suffix}</strong>
        ) : (
          <input
            type="text" inputMode="decimal" value={value}
            onChange={(e) => {
              const raw = e.target.value.replace(',', '.');
              if (raw === '' || raw === '.' || /^\d*\.?\d{0,2}$/.test(raw)) {
                onChange(raw);
              }
            }}
            onBlur={() => onChange(D2(value))}
            style={{ width: '100px', textAlign: 'right', padding: '4px 8px' }}
          />
        )}
      </div>
    );
  };

  const textField = (label, value, onChange, lock) => {
    const locked = lock !== undefined ? lock : soloLectura;
    return (
      <div style={{ marginBottom: 'var(--spacing-3)' }}>
        <label className="text-sm text-secondary">{label}</label>
        {locked ? (
          <div className="text-sm" style={{ padding: '6px 0' }}>{value || '—'}</div>
        ) : (
          <input type="text" value={value} onChange={(e) => onChange(e.target.value)} style={{ width: '100%' }} />
        )}
      </div>
    );
  };

  const titulo = modo === 'crear' ? 'Liquidar paquete'
    : modo === 'ver' ? 'Liquidación guardada'
    : 'Modificar liquidación';

  if (cargando) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
          <p className="text-secondary">Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {modo === 'ver' ? <Lock size={18} /> : <DollarSign size={18} />}
            {titulo}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            {liquidacionId && (
              <FacturarButton
                liquidacionId={liquidacionId}
                facturasElectronicas={facturasElectronicas}
                onFacturada={(data) => {
                  const nuevas = Array.isArray(data) ? data : [data];
                  setFacturasElectronicas((prev) => {
                    const porTipo = Object.fromEntries(prev.map((f) => [f.tipo, f]));
                    nuevas.forEach((f) => { porTipo[f.tipo] = f; });
                    return Object.values(porTipo);
                  });
                }}
              />
            )}
            <button className="btn-ghost" onClick={onClose}><X size={18} /></button>
          </div>
        </div>

        {facturada && (
          <div style={{ background: '#e8f5e9', padding: '8px 12px', borderRadius: '6px', marginBottom: 'var(--spacing-3)', fontSize: '0.85rem', color: '#2e7d32' }}>
            {modo === 'editar'
              ? 'Liquidación facturada — solo se pueden modificar los costos internos y campos FAC.'
              : 'Esta liquidación ya fue facturada. Los ingresos no pueden modificarse, pero puedes editar los costos internos.'}
          </div>
        )}

        <div>
          {/* Info del paquete */}
          <div style={{ display: 'flex', gap: 'var(--spacing-4)', marginBottom: 'var(--spacing-4)', background: 'var(--color-bg-secondary)', padding: 'var(--spacing-3)', borderRadius: 'var(--radius-md)' }}>
            <div>
              <span className="text-xs text-secondary">Cliente</span>
              <div><strong>{paquete.cliente_detalle?.nombre}</strong></div>
            </div>
            <div>
              <span className="text-xs text-secondary">Guía</span>
              <div><strong>{esConsolidado && paquete.consolidacion_detalle?.guia_agrupada ? paquete.consolidacion_detalle.guia_agrupada : (paquete.guia || paquete.tracking)}</strong></div>
            </div>
            <div>
              <span className="text-xs text-secondary">Origen</span>
              <div>{paquete.pais_origen === 'usa' ? 'Miami' : 'China'}</div>
            </div>
            {esConsolidado && (
              <div>
                <span className="text-xs text-secondary">Paquetes</span>
                <div><strong>{paquetes.length}</strong></div>
              </div>
            )}
          </div>

          {/* Ingresos */}
          <div style={{ marginBottom: 'var(--spacing-4)' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 'var(--spacing-2)' }}>
              <Calculator size={16} /> Ingresos (cobro al cliente)
            </h4>
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--spacing-3)' }}>
              {field('Peso (libras)', peso, setPeso, { lock: ingresosLock, unit: 'lb' })}
              {field('Tarifa por libra', tarifaPorLibra, null, { readOnly: true })}
              {field('Total envío', flete, null, { readOnly: true })}
              {field('Servicio aduanero', servAduanero, setServAduanero, { lock: ingresosLock })}
              {field('Consolidación', consolMonto, setConsolMonto, { lock: ingresosLock })}
              {field('Entrega a domicilio', entregaDom, (v) => { entregaManual.current = true; setEntregaDom(v); }, { lock: ingresosLock })}
              {!ingresosLock && umbralDomicilio > 0 && (
                <div className="text-xs text-secondary" style={{ padding: '2px 0 6px' }}>
                  {domicilioIncluido
                    ? `Incluida en la tarifa (paquetes desde ${umbralDomicilio} lb). Déjala en 0.`
                    : `Paquetes de menos de ${umbralDomicilio} lb: la entrega a domicilio no va incluida y se cobra. Ponla en 0 solo si decides no cobrarla.`}
                </div>
              )}
              {otros.map((o, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)', padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}>
                  {ingresosLock ? (
                    <>
                      <span className="text-sm">{o.concepto}</span>
                      <strong style={{ fontVariantNumeric: 'tabular-nums' }}>${D2(o.monto).toFixed(2)}</strong>
                    </>
                  ) : (
                    <>
                      <input type="text" value={o.concepto} placeholder="Concepto (ej. Reempaque)" maxLength={200}
                        onChange={(e) => setOtros((l) => l.map((x, j) => (j === i ? { ...x, concepto: e.target.value } : x)))}
                        style={{ flex: 1, minWidth: 0, padding: '4px 8px' }} />
                      <input type="text" inputMode="decimal" value={o.monto}
                        onChange={(e) => {
                          const raw = e.target.value.replace(',', '.');
                          if (raw === '' || raw === '.' || /^\d*\.?\d{0,2}$/.test(raw)) {
                            setOtros((l) => l.map((x, j) => (j === i ? { ...x, monto: raw } : x)));
                          }
                        }}
                        style={{ width: '100px', textAlign: 'right', padding: '4px 8px' }} />
                      <button type="button" className="btn-ghost" aria-label="Quitar concepto" title="Quitar"
                        onClick={() => setOtros((l) => l.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              ))}
              {!ingresosLock && (
                <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap', padding: '8px 0 4px' }}>
                  {servicios.filter((x) => x.activo && !SERVICIOS_CON_CAMPO.includes(x.codigo)).map((x) => (
                    <button key={x.codigo} type="button" className="btn-secondary btn-sm"
                      onClick={() => setOtros((l) => [...l, { concepto: x.nombre, monto: D2(x.precio) }])}>
                      <Plus size={12} /> {x.nombre}
                    </button>
                  ))}
                  <button type="button" className="btn-secondary btn-sm" onClick={() => setOtros((l) => [...l, { concepto: '', monto: '' }])}>
                    <Plus size={12} /> Otro concepto
                  </button>
                </div>
              )}
              <div className="text-xs text-secondary" style={{ paddingBottom: '4px' }}>
                Los conceptos adicionales llevan IVA y se suman al subtotal.
              </div>
              <div style={{ borderTop: '2px solid var(--color-border)', marginTop: '4px' }}>
                {field('Subtotal', subtotal, null, { readOnly: true })}
                {field(`IVA (${D2(ivaPct * 100)}%)`, iva, null, { readOnly: true })}
                {field('Total Courier', totalCourier, null, { readOnly: true })}
              </div>
              <div style={{ borderTop: '2px solid var(--color-border)', marginTop: '4px' }}>
                {field('Aduana', aduana, setAduana, { lock: ingresosLock })}
                {field('Fodinfa', fodinfa, setFodinfa, { lock: ingresosLock })}
                {field('Gastos Bancarios', gastosBancarios, setGastosBancarios, { lock: ingresosLock })}
              </div>
              <div style={{ borderTop: '3px solid var(--color-primary)', marginTop: '4px', paddingTop: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong style={{ fontSize: '16px' }}>Total a Pagar</strong>
                  <strong style={{ fontSize: '18px', color: 'var(--color-primary)' }}>${totalAPagar.toFixed(2)}</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Costos internos — solo admin */}
          {rol === 'admin' && (
            <div style={{ marginBottom: 'var(--spacing-4)' }}>
              <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 'var(--spacing-2)' }}>
                <FileText size={16} /> Costos internos
              </h4>
              <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--spacing-3)' }}>
                {field('Costo MTC', costoMtc, setCostoMtc, { lock: costosLock })}
                {field('Última milla', ultimaMilla, setUltimaMilla, { lock: costosLock })}
                {field('Consolidación (costo)', consolCosto, setConsolCosto, { lock: costosLock })}
                {field(`IVA costos (${D2(ivaPct * 100)}%)`, ivaCostos, null, { readOnly: true })}
                {field('Reembolso Aduana (costo)', reemAdCosto, setReemAdCosto, { lock: costosLock })}
                {field('Reembolso Gastos Bancarios (costo)', reemGbCosto, setReemGbCosto, { lock: costosLock })}
                {field('Flete volumétrico', fleteVol, setFleteVol, { lock: costosLock })}
                <div style={{ borderTop: '2px solid var(--color-border)', marginTop: '4px' }}>
                  {field('Total Costo', totalCosto, null, { readOnly: true })}
                  {field('Utilidad', utilidad, null, { readOnly: true })}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
                    <span className="text-sm">Margen</span>
                    <strong style={{ color: margen >= 0 ? 'var(--color-success, #22c55e)' : '#ef4444' }}>{margen.toFixed(2)}%</strong>
                  </div>
                </div>

                <div style={{ marginTop: 'var(--spacing-3)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-2)' }}>
                    {textField('FAC MTC Servicio', facMtcServ, setFacMtcServ, costosLock)}
                    {textField('FAC MTC Reembolso', facMtcReem, setFacMtcReem, costosLock)}
                    {textField('FAC Ecuafly Servicio', facEcuServ, setFacEcuServ, costosLock)}
                    {textField('FAC Ecuafly Reembolso', facEcuReem, setFacEcuReem, costosLock)}
                  </div>
              </div>
            </div>
          )}

          {/* Notas + Forma cobro */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
            <div>
              <label className="text-sm text-secondary">Notas para el cliente</label>
              {ingresosLock ? (
                <div className="text-sm" style={{ padding: '6px 0' }}>{notasCliente || '—'}</div>
              ) : (
                <textarea value={notasCliente} onChange={(e) => setNotasCliente(e.target.value)}
                  rows={2} style={{ width: '100%' }} />
              )}
            </div>
            <div>
              <label className="text-sm text-secondary">
                Forma de cobro {guardandoFormaCobro && <span className="text-xs">(guardando...)</span>}
              </label>
              {/* A propósito SIEMPRE editable, sin importar si ya está facturado:
                  es un control interno de cómo pagó el cliente, no una operación
                  de facturación — no debería bloquearse ni depender del modo. */}
              <select value={formaCobro} onChange={(e) => cambiarFormaCobro(e.target.value)}
                disabled={guardandoFormaCobro} style={{ width: '100%' }}>
                <option value="pendiente">Pendiente</option>
                <option value="transferencia">Transferencia</option>
                <option value="payphone">Payphone (tarjeta)</option>
                <option value="efectivo">Efectivo</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 'var(--spacing-3)', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {modo === 'ver' && liquidacionId && (
              <div style={{ marginRight: 'auto', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                <button className="btn-secondary" onClick={enviarLiquidacion} disabled={enviandoLiq}>
                  <Send size={14} /> {enviandoLiq ? 'Enviando...' : envio.en ? 'Reenviar liquidación al cliente' : 'Enviar liquidación al cliente'}
                </button>
                {envio.en && (
                  <span className="text-xs text-secondary">
                    Enviada a {envio.a} el {fechaEnvio(envio.en)}
                  </span>
                )}
                {envio.en && envio.desactualizado && (
                  <span className="text-xs" style={{ color: 'var(--color-warning, #b45309)', fontWeight: 600 }}>
                    La modificaste después de enviarla: reenvíala para que el cliente tenga los valores actuales.
                  </span>
                )}
              </div>
            )}
            {modo === 'editar' && envio.en && (
              <span className="text-xs text-secondary" style={{ marginRight: 'auto', alignSelf: 'center' }}>
                Ya se envió al cliente el {fechaEnvio(envio.en)}. Al guardar cambios tendrás que reenviarla.
              </span>
            )}
            <button className="btn-secondary" onClick={onClose}>
              {modo === 'ver' ? 'Cerrar' : 'Cancelar'}
            </button>
            {modo === 'ver' && (
              <button className="btn-primary" onClick={() => setModo('editar')}>
                <Edit3 size={14} /> Modificar
              </button>
            )}
            {(modo === 'crear' || modo === 'editar') && (
              <button className="btn-primary" onClick={guardar} disabled={guardando || totalAPagar <= 0}>
                {guardando ? 'Guardando...' : modo === 'editar' ? 'Guardar cambios' : 'Guardar liquidación'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
